import { useEffect, useState } from 'react';
import { Modal } from '../../components/primitives';
import { Icon } from '../../components/icons/Icon';
import { cx } from '../../lib/cx';
import guide from './InstallGuide.module.css';
import styles from './ThemeGuide.module.css';

/**
 * Animovaný návod „Jak změnit vzhled" (světlý režim + bílé dokumenty).
 * Stejný princip jako InstallGuide — maketa aplikace v CSS, kroky se samy
 * přehrávají dokola. Cesta v aplikaci: avatar vpravo nahoře → ⚙ Nastavení
 * účtu → Vzhled (AccountSettingsModal).
 */

const STEPS: { title: string; text: string }[] = [
  {
    title: 'Klikněte na své iniciály vpravo nahoře',
    text: 'Kolečko s iniciálami je v hlavičce aplikace — na počítači i v telefonu.',
  },
  { title: 'Zvolte „⚙ Nastavení účtu"', text: 'V rozbalené nabídce pod jménem a e-mailem.' },
  {
    title: 'Vzhled → Světlý režim aplikace',
    text: 'Menu a panely budou bílé místo tmavě modrých. Změna platí hned.',
  },
  {
    title: 'Vzhled → Bílé dokumenty',
    text: 'Popisky a tabulky v dokumentech čistě bílé místo béžových — i v PDF. Obě volby jsou nezávislé.',
  },
  {
    title: 'Hotovo',
    text: 'Nastavení se ukládá k účtu, takže platí na všech zařízeních. Vrátit jde stejnou cestou.',
  },
];

const STEP_MS = 2600;
const LAST_STEP_MS = 3400;

const Tap = () => <span className={guide.tap} aria-hidden />;

export function ThemeGuideButton({ className }: { className?: string }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" className={cx(guide.trigger, className)} onClick={() => setOpen(true)}>
        <Icon name="contrast" size={14} /> Jak změnit vzhled (světlý režim)
      </button>
      <ThemeGuideModal open={open} onClose={() => setOpen(false)} />
    </>
  );
}

function ThemeGuideModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [step, setStep] = useState(0);
  const last = STEPS.length - 1;

  useEffect(() => {
    if (!open) return;
    const t = window.setTimeout(() => setStep((s) => (s >= last ? 0 : s + 1)), step >= last ? LAST_STEP_MS : STEP_MS);
    return () => window.clearTimeout(t);
  }, [open, step, last]);

  return (
    <Modal open={open} onClose={onClose} title="Změna vzhledu aplikace" size="lg">
      <div className={guide.layout}>
        <div className={guide.stage} aria-hidden>
          <AppScene step={step} />
        </div>

        <ol className={guide.steps}>
          {STEPS.map((s, i) => (
            <li key={s.title}>
              <button
                type="button"
                className={cx(guide.step, i === step && guide.stepActive, i < step && guide.stepDone)}
                onClick={() => setStep(i)}
              >
                <span className={guide.stepNo}>{i === last ? '✓' : i + 1}</span>
                <span className={guide.stepBody}>
                  <span className={guide.stepTitle}>{s.title}</span>
                  <span className={guide.stepText}>{s.text}</span>
                </span>
              </button>
            </li>
          ))}
        </ol>
      </div>

      <p className={guide.note}>
        Výchozí vzhled je tmavý. Světlý režim se hodí do kanceláře a na přímé slunce na stavbě, bílé dokumenty
        pro úspornější tisk. Vzhled se mění po přihlášení a každý účet má svou volbu.
      </p>
    </Modal>
  );
}

/* ───────────── maketa aplikace ───────────── */

function AppScene({ step }: { step: number }) {
  const light = step >= 2;
  const docWhite = step >= 3;
  const settingsOpen = step === 2 || step === 3;
  return (
    <div className={cx(styles.app, light && styles.light)}>
      <div className={styles.topBar}>
        <span className={styles.brand}>
          <span className={styles.logo}>S</span> SiteDoc
        </span>
        <span className={styles.topRight}>
          <span className={styles.pill} />
          <span className={cx(styles.avatar, step === 0 && styles.avatarHot)}>
            JN
            {step === 0 && <Tap />}
          </span>
        </span>
      </div>

      <div className={styles.body}>
        <div className={styles.side}>
          {Array.from({ length: 7 }, (_, i) => (
            <div key={i} className={cx(styles.sideItem, i === 2 && styles.sideActive)} />
          ))}
        </div>
        <div className={styles.main}>
          <div className={cx(styles.doc, docWhite && styles.docWhite)}>
            <div className={styles.docTitle} />
            {Array.from({ length: 4 }, (_, i) => (
              <div key={i} className={styles.docRow}>
                <span className={styles.docLabel} />
                <span className={styles.docValue} />
              </div>
            ))}
            <div className={styles.docTable}>
              {Array.from({ length: 3 }, (_, i) => (
                <div key={i} className={cx(styles.docTr, i === 0 && styles.docTh)} />
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className={cx(styles.menu, step === 1 && styles.menuOpen)}>
        <div className={styles.menuHead}>
          <span className={styles.avatar}>JN</span>
          <span className={styles.menuMeta}>
            <span className={styles.menuName}>Jan Novák</span>
            <span className={styles.menuMail}>jan@firma.cz</span>
          </span>
        </div>
        <div className={cx(styles.menuRow, step === 1 && guide.rowHot)}>
          ⚙ Nastavení účtu
          {step === 1 && <Tap />}
        </div>
        <div className={styles.menuRow}>⏻ Odhlásit se</div>
      </div>

      <div className={cx(styles.scrim, settingsOpen && styles.scrimShow)}>
        <div className={styles.dialog}>
          <div className={styles.dialogTitle}>⚙ Nastavení účtu</div>
          <div className={styles.dialogSection}>Vzhled</div>
          <div className={cx(styles.check, step === 2 && guide.rowHot)}>
            <span className={cx(styles.box, step >= 2 && styles.boxOn)}>{step >= 2 ? '✓' : ''}</span>
            Světlý režim aplikace
            {step === 2 && <Tap />}
          </div>
          <div className={cx(styles.check, step === 3 && guide.rowHot)}>
            <span className={cx(styles.box, step >= 3 && styles.boxOn)}>{step >= 3 ? '✓' : ''}</span>
            Bílé dokumenty
            {step === 3 && <Tap />}
          </div>
          <div className={styles.dialogNote}>Ukládá se k účtu — platí na všech zařízeních.</div>
        </div>
      </div>
    </div>
  );
}
